import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, Alert, Tooltip, IconButton, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { FileText, CalendarX, FilePlus, HardDrive, Newspaper, FileMinus, PauseCircle, Info, ArrowLeft } from 'lucide-react'; // Ícones para os cards
import { useNavigate } from 'react-router-dom';
import ContractService from '../../services/contractService'; // Importar serviço
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { ptBRGridLocale } from '../../utils/gridLocalization';

// Interface para os dados do dashboard
interface DashboardStats {
  activeContracts: number;
  noSuspensionLastYear: number;
  noAdditiveLastYear: number;
  equipmentsInUse: number;
  newContractsLast30Days: number;
  terminatedContractsLast30Days: number;
  suspendedThisMonth: number;
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<string>('');
  const [contracts, setContracts] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showCards, setShowCards] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Estados para o grid de contratos
  const [selectedCard, setSelectedCard] = useState<string | null>(null);
  const [contractsLoading, setContractsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(12);
  const [totalCount, setTotalCount] = useState(0);

  const fetchContracts = async (type: string, page: number) => {
    try {
      setLoading(true);
      const response = await ContractService.getDashboardContracts(type, page, 12);
      setContracts(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Erro ao buscar contratos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await ContractService.getDashboardStats();
        setStats(data);
      } catch (err: any) {
        setError(err.message || 'Falha ao carregar dados do dashboard.');
        console.error("Erro no dashboard:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const handleCardClick = async (type: string, title: string) => {
    setSelectedType(type);
    setSelectedTitle(title);
    setShowCards(false);
    setPage(1);
    try {
      const response = await ContractService.getDashboardContracts(type, 1, 12);
      setContracts(response.data);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error('Erro ao buscar contratos:', error);
    }
  };

  const handleBackToCards = () => {
    setShowCards(true);
    setSelectedType(null);
    setSelectedTitle('');
    setContracts([]);
  };

  const handlePageChange = (newPage: number): void => {
    setPage(newPage);
    const type = getTypeFromTitle(selectedTitle);
    if (type) {
      fetchContracts(type, newPage);
    }
  };

  const getTypeFromTitle = (title: string): string => {
    const typeMap: { [key: string]: string } = {
      'Contratos Ativos': 'active',
      'Contratos Pendentes de Suspensão (>1 Ano)': 'noSuspension',
      'Contratos Pendentes de Aditivo (>1 Ano)': 'noAdditive',
      'Equipamentos em Uso': 'equipmentsInUse',
      'Novos Contratos (Últimos 30 dias)': 'newContracts',
      'Contratos Encerrados (Últimos 30 dias)': 'terminated',
      'Contratos em Suspensão (Este mês)': 'suspended'
    };
    return typeMap[title] || 'active';
  };

  const getColumns = (): GridColDef[] => {
    const baseColumns: GridColDef[] = [
      {
        field: 'empresaNome',
        headerName: 'Empresa / Pessoa',
        flex: 1,
        minWidth: 300,
        renderCell: (params) => (
          <Box>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {params.row.empresaNome}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              {params.row.pessoaNome}
            </Typography>
          </Box>
        )
      },
      {
        field: 'contract_start_date',
        headerName: 'Data Início',
        width: 120,
        valueFormatter: (params) => formatDate(params.value)
      },
      {
        field: 'currentPaymentTypeName',
        headerName: 'Tipo Pagamento',
        width: 150
      },
      {
        field: 'currentValue',
        headerName: 'Valor',
        width: 120,
        valueFormatter: (params) => formatCurrency(params.value)
      }
    ];

    // Adicionar coluna de última suspensão apenas para os tipos relacionados
    if (selectedType === 'noSuspension' || selectedType === 'suspended') {
      baseColumns.push({
        field: 'lastSuspensionDate',
        headerName: 'Última Suspensão',
        width: 150,
        valueFormatter: (params) => params.value ? formatDate(params.value) : 'Sem registro'
      });
    }

    // Adicionar coluna de último aditivo para contratos pendentes de aditivo
    if (selectedType === 'noAdditive') {
      baseColumns.push({
        field: 'lastAdditiveDate',
        headerName: 'Último Aditivo',
        width: 150,
        valueFormatter: (params) => params.value ? formatDate(params.value) : 'Sem registro'
      });
    }

    return baseColumns;
  };

  const renderStatCard = (
    title: string, 
    value: number | undefined, 
    icon: React.ReactNode,
    color: string = 'primary.main',
    explanation?: string
  ) => (
    <Grid item xs={12} sm={6} md={4}>
      <Card 
        sx={{
          position: 'relative',
          p: 2,
          cursor: 'pointer',
          borderLeft: `4px solid ${color}`,
          backgroundColor: '#fff',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: (theme) => theme.shadows[4],
            bgcolor: (theme) => alpha(theme.palette.background.paper, 0.9)
          },
        }}
        onClick={() => handleCardClick(getTypeFromTitle(title), title)}
      >
        {explanation && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <Tooltip title={explanation} arrow placement="top">
              <Info size={14} style={{ color: 'rgba(0, 0, 0, 0.54)', cursor: 'help' }} />
            </Tooltip>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Box 
            className="card-icon"
            sx={{ 
              mr: 2,
              color: color,
              display: 'flex',
              transition: 'transform 0.2s ease-in-out'
            }}
          >
            {React.cloneElement(icon as React.ReactElement, { size: 24 })}
          </Box>
          <Box>
            <Typography variant="h4" sx={{ mb: 0.5, fontSize: '2rem', fontWeight: 500, color: 'text.primary' }}>
              {value ?? '-'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
              {title}
            </Typography>
          </Box>
        </Box>
      </Card>
    </Grid>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  const renderCard = (title: string, value: number, type: string) => (
    <Card 
      sx={{ 
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'action.hover',
        },
      }}
      onClick={() => handleCardClick(type, title)}
    >
      <CardContent>
        <Typography variant="h6" component="div">
          {value}
        </Typography>
        <Typography color="text.secondary">
          {title}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Typography 
        variant="h5" 
        component="h1" 
        sx={{ 
          mb: 4,
          fontSize: '1.5rem',
          fontWeight: '400'
        }}
      >
        Dashboard
      </Typography>

      {showCards ? (
        <Grid container spacing={3}>
          {stats && (
            <>
              {renderStatCard(
                'Contratos Ativos',
                stats.activeContracts,
                <FileText />,
                '#1976d2', // Azul
                'Contratos que estão atualmente em vigor, sem data de término registrada.'
              )}
              {renderStatCard(
                'Contratos Pendentes de Suspensão (>1 Ano)',
                stats.noSuspensionLastYear,
                <CalendarX />,
                '#ed6c02', // Laranja
                'Contratos ativos que não tiveram registro de suspensão nos últimos 12 meses.'
              )}
              {renderStatCard(
                'Contratos Pendentes de Aditivo (>1 Ano)',
                stats.noAdditiveLastYear,
                <FilePlus />,
                '#0288d1', // Azul claro
                'Contratos ativos que não tiveram aditivos contratuais nos últimos 12 meses.'
              )}
              {renderStatCard(
                'Equipamentos em Uso',
                stats.equipmentsInUse,
                <HardDrive />,
                '#0288d1', // Azul claro
                'Total de equipamentos atualmente alocados em contratos ativos sem data de devolução.'
              )}
              {renderStatCard(
                'Novos Contratos (Últimos 30 dias)',
                stats.newContractsLast30Days,
                <Newspaper />,
                '#9c27b0', // Roxo
                'Contratos que foram iniciados nos últimos 30 dias.'
              )}
              {renderStatCard(
                'Contratos Encerrados (Últimos 30 dias)',
                stats.terminatedContractsLast30Days,
                <FileMinus />,
                '#d32f2f', // Vermelho
                'Contratos que foram encerrados nos últimos 30 dias.'
              )}
              {renderStatCard(
                'Contratos em Suspensão (Este mês)',
                stats.suspendedThisMonth,
                <PauseCircle />,
                '#ff9800', // Laranja
                'Contratos que tiveram registro de suspensão no mês atual.'
              )}
            </>
          )}
        </Grid>
      ) : (
        <>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 3, 
            justifyContent: 'space-between' 
          }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 500 }}>
              {selectedTitle}
            </Typography>
            <Button
              variant="contained"
              size="medium"
              startIcon={<ArrowLeft size={20} />}
              onClick={handleBackToCards}
              sx={{
                textTransform: 'uppercase',
                backgroundColor: '#0D47A1',
                borderRadius: '4px',
                color: '#fff',
                px: 2,
                '&:hover': {
                  backgroundColor: '#0A3882'
                },
                fontSize: '0.875rem'
              }}
            >
              Voltar para Dashboard
            </Button>
          </Box>

          <DataGrid
            rows={contracts}
            columns={getColumns()}
            autoHeight
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableRowSelectionOnClick
            hideFooterSelectedRowCount
            paginationMode="server"
            rowCount={totalPages * 12}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 12, page: page - 1 },
              },
            }}
            pageSizeOptions={[12]}
            onPaginationModelChange={(model) => handlePageChange(model.page + 1)}
            loading={loading}
            localeText={ptBRGridLocale}
            sx={{
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontSize: '0.875rem',
                  fontWeight: 600
                }
              },
              '& .MuiDataGrid-cell': {
                fontSize: '0.875rem',
                cursor: 'pointer'
              },
              '& .MuiTablePagination-root': {
                fontSize: '0.875rem'
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: '#f5f5f5',
                '& .MuiTablePagination-displayedRows': {
                  fontSize: '0.875rem'
                },
                '& .MuiTablePagination-selectLabel': {
                  fontSize: '0.875rem'
                }
              },
              '& .MuiDataGrid-overlay': {
                fontSize: '0.875rem'
              }
            }}
          />
        </>
      )}
    </Box>
  );
};

export default DashboardPage; 